import React, { useState, useEffect } from "react";
import "./parametre.css";
import HeaderFirst from "utils/HeaderFirst";
import { useSelector } from "react-redux";
import { getUser } from "redux/features/userSliceWithTokenValidation";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import InputDetail from "components/Inputs/InputDetail";
import { useGetDossierByIdQuery } from "actions/dossiers/queries";
import { useUpdateDossierClientMutation } from "actions/dossiers/mutations";
import { useCleanupOrphanEcrituresMutation } from "actions/comptabilite/mutations";
import { DEFAULT_API } from "routes/api/endpoints";
import SecurityMFACard from "components/MFA/SecurityMFACard";
import PlanComptable from "pagesDossierClient/comptabilite/components/PlanComptable";
import ComptePaiements from "pagesDossierClient/comptePaiement/ComptePaiements";
import PartageDossier from "pagesDossierClient/partage/PartageDossier";
import JournalPage from "pagesDossierClient/journal/JournalPage";
import MigrationOdooPage from "pagesDossierClient/migrationOdoo/MigrationOdooPage";
import {
  RiBuilding4Line,
  RiFileTextLine,
  RiSettings4Line,
  RiShieldCheckLine,
  RiCameraLine,
  RiAwardLine,
  RiCheckLine,
  RiEraserLine,
  RiBookOpenLine,
  RiUploadCloud2Line,
  RiHistoryLine,
  RiUserSharedLine,
  RiWalletLine,
} from "react-icons/ri";

const mediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("blob:")) return path;
  try {
    return new URL(path, DEFAULT_API).href;
  } catch {
    return path;
  }
};

const ParametrePage = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [subTab, setSubTab] = useState("entreprise");
  const { control, handleSubmit, reset, watch } = useForm();
  const userStatus = useSelector(getUser);
  const { clientID: id } = useParams();
  const { data: dossierData, refetch } = useGetDossierByIdQuery({ id }, { skip: !id });
  const [updateDossierMutation, { isLoading: isUpdating }] = useUpdateDossierClientMutation();
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [cachetUrl, setCachetUrl] = useState("");
  const [cachetFile, setCachetFile] = useState(null);
  const [cleanupOrphans, { isLoading: isCleanupLoading }] = useCleanupOrphanEcrituresMutation();
  const [orphansCount, setOrphansCount] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const watchMode = watch("mode_numerotation_piece", dossierData?.mode_numerotation_piece || "PAR_JOURNAL");

  useEffect(() => {
    if (dossierData) {
      const dossier = dossierData;
      setLogoUrl(mediaUrl(dossier.logo));
      setCachetUrl(mediaUrl(dossier.cachet));
      reset({
        nom_entreprise: dossier.nom_entreprise || "",
        adresse: dossier.adresse || "",
        telephone: dossier.telephone || "",
        email: dossier.email || "",
        description: dossier.description || "",
        nina: dossier.nina || dossier.imma_siret || "",
        cofi: dossier.cofi || "",
        imma_idt_tva: dossier.imma_idt_tva || "",
        rib: dossier.rib || "",
        banque_nom: dossier.banque_nom || "",
        mode_numerotation_piece: dossier.mode_numerotation_piece || "PAR_JOURNAL",
        format_numerotation_piece: dossier.format_numerotation_piece || "[JRN]-[AAAAMM]-[SEQ]",
      });
    }
  }, [dossierData, reset]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoUrl(URL.createObjectURL(file));
    }
  };

  const handleCachetChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCachetFile(file);
      setCachetUrl(URL.createObjectURL(file));
    }
  };

  const handleCancel = async () => {
    await refetch();
    toast.info("Modifications annulées");
  };

  const handleCheckOrphans = async () => {
    try {
      const result = await cleanupOrphans({ dry_run: true }).unwrap();
      setOrphansCount(result.orphans_found);
      if (result.orphans_found === 0) {
        toast.success("Aucune écriture orpheline détectée. La base est parfaitement propre.");
      } else {
        toast.info(`${result.orphans_found} écriture(s) orpheline(s) détectée(s).`);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification:", error);
      toast.error("Erreur lors de la vérification des écritures orphelines.");
    }
  };

  const handleCleanupOrphans = async () => {
    try {
      const result = await cleanupOrphans({ dry_run: false }).unwrap();
      toast.success(result.message);
      setOrphansCount(0);
      setShowConfirm(false);
    } catch (error) {
      console.error("Erreur lors du nettoyage:", error);
      toast.error("Erreur lors du nettoyage des écritures orphelines.");
    }
  };

  const nom = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <h4 className="titre-dossier mb-0">
        Paramètres du dossier {dossierData?.nom_entreprise ? `: ${dossierData.nom_entreprise}` : ""}
      </h4>
    </div>
  );

  const onSubmit = async (formData) => {
    try {
      if (dossierData) {
        const formDataToSend = new FormData();
        formDataToSend.append("archived", false);
        formDataToSend.append("id", id);
        formDataToSend.set("nom_entreprise", formData.nom_entreprise || "");
        formDataToSend.set("adresse", formData.adresse || "");
        formDataToSend.set("telephone", formData.telephone || "");
        formDataToSend.set("description", formData.description || "");

        if (formData.email) {
          formDataToSend.set("email", formData.email);
        }

        formDataToSend.set("nina", formData.nina || "");
        formDataToSend.set("cofi", formData.cofi || "");
        formDataToSend.set("imma_idt_tva", formData.imma_idt_tva || "");
        formDataToSend.set("rib", formData.rib || "");
        formDataToSend.set("banque_nom", formData.banque_nom || "");
        formDataToSend.set("mode_numerotation_piece", formData.mode_numerotation_piece || "PAR_JOURNAL");
        formDataToSend.set("format_numerotation_piece", formData.format_numerotation_piece || "[JRN]-[AAAAMM]-[SEQ]");

        if (logoFile instanceof File) {
          formDataToSend.set("logo", logoFile);
        }
        if (cachetFile instanceof File) {
          formDataToSend.set("cachet", cachetFile);
        }

        await updateDossierMutation({
          id: id,
          body: formDataToSend,
          token: userStatus?.access,
        }).unwrap();

        refetch();
        toast.success("Informations du dossier mises à jour avec succès !");
      }
    } catch (error) {
      console.error("Échec de la modification:", error);
      toast.error("Erreur lors de la mise à jour des informations du dossier.");
    }
  };

  return (
    <div
      className="global parametre-container"
      style={{
        backgroundColor: "#fbfbfc",
        minHeight: "650px",
        borderRadius: "20px",
        padding: "24px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
      }}
    >
      <div className="pb-3 mb-3 border-bottom">
        <HeaderFirst
          nom={nom}
          description={<div>Gérez les coordonnées, les mentions légales et la comptabilité de ce dossier</div>}
        />
      </div>

      {/* ── ONGLETS FLUIDES RESPONSIVE MONO-LIGNE ── */}
      <div className="param-tabs-container">
        <button
          type="button"
          onClick={() => setActiveTab("general")}
          className={`param-tab-btn ${activeTab === "general" ? "active" : ""}`}
        >
          <RiBuilding4Line size={16} />
          <span className="d-none d-lg-inline">Informations Générales</span>
          <span className="d-inline d-lg-none">Général</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("comptes")}
          className={`param-tab-btn ${activeTab === "comptes" ? "active" : ""}`}
        >
          <RiWalletLine size={16} />
          <span className="d-none d-lg-inline">Mes comptes</span>
          <span className="d-inline d-lg-none">Comptes</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("plan")}
          className={`param-tab-btn ${activeTab === "plan" ? "active" : ""}`}
        >
          <RiBookOpenLine size={16} />
          <span className="d-none d-lg-inline">Plan comptable</span>
          <span className="d-inline d-lg-none">Plan</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("migration")}
          className={`param-tab-btn ${activeTab === "migration" ? "active" : ""}`}
        >
          <RiUploadCloud2Line size={16} />
          <span className="d-none d-lg-inline">Migration Odoo</span>
          <span className="d-inline d-lg-none">Migration</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("journal")}
          className={`param-tab-btn ${activeTab === "journal" ? "active" : ""}`}
        >
          <RiHistoryLine size={16} />
          <span className="d-none d-lg-inline">Journal d&apos;activité</span>
          <span className="d-inline d-lg-none">Journal</span>
        </button>

        {dossierData?.user_role === 'ADMIN' && (
          <button
            type="button"
            onClick={() => setActiveTab("partage")}
            className={`param-tab-btn ${activeTab === "partage" ? "active" : ""}`}
          >
            <RiUserSharedLine size={16} />
            <span className="d-none d-lg-inline">Partage de dossier</span>
            <span className="d-inline d-lg-none">Partage</span>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {activeTab === "general" && (
          <div className="param-general-layout">
            {/* ── SOUS-MENU LATERAL ── */}
            <div className="param-subnav">
              <button
                type="button"
                onClick={() => setSubTab("entreprise")}
                className={`param-subnav-btn ${subTab === "entreprise" ? "active" : ""}`}
              >
                <RiBuilding4Line size={15} />
                <span>Entreprise & Coordonnées</span>
              </button>
              <button
                type="button"
                onClick={() => setSubTab("mentions")}
                className={`param-subnav-btn ${subTab === "mentions" ? "active" : ""}`}
              >
                <RiFileTextLine size={15} />
                <span>Mentions légales & Facturation</span>
              </button>
              <button
                type="button"
                onClick={() => setSubTab("comptabilite")}
                className={`param-subnav-btn ${subTab === "comptabilite" ? "active" : ""}`}
              >
                <RiSettings4Line size={15} />
                <span>Comptabilité & Maintenance</span>
              </button>
              <button
                type="button"
                onClick={() => setSubTab("securite")}
                className={`param-subnav-btn ${subTab === "securite" ? "active" : ""}`}
              >
                <RiShieldCheckLine size={15} />
                <span>Sécurité & 2FA</span>
              </button>
            </div>

            {/* ── CONTENU DU SOUS-ONGLET ── */}
            <div className="param-subcontent" key={subTab}>
        {/* ── ONGLET 1 : ENTREPRISE & COORDONNÉES ── */}
        {subTab === "entreprise" && (
          <div className="d-flex flex-column gap-4">
            <div
              className="p-4 rounded-4"
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #eef2f6",
                borderRadius: "16px",
              }}
            >
              <h6 className="mb-3" style={{ fontWeight: 700, color: "#0f172a" }}>
                Identité de l&apos;entreprise
              </h6>

              <div className="row g-3">
                <div className="col-md-6">
                  <Controller
                    name="nom_entreprise"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="Nom de l'entreprise" placeholder="Ex: Bakeli Tech" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="adresse"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="Adresse du siège" placeholder="Ex: Point E, Dakar" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="telephone"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="Numéro de téléphone" placeholder="Ex: +221 33 800 00 00" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="Adresse e-mail" placeholder="Ex: compta@entreprise.sn" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-12">
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail
                        label="Description ou objet social de l'entreprise"
                        placeholder="Brève description de l'activité..."
                        isTextarea={true}
                        color="#0f172a"
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Logo & Cachet */}
              <div className="row g-4 mt-2 pt-3 border-top">
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-dark mb-2 d-flex align-items-center gap-1">
                    <RiCameraLine size={16} style={{ color: "#009688" }} />
                    <span>Logo de l&apos;entreprise</span>
                  </label>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                        border: "1px dashed #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {logoUrl ? (
                        <img src={logoUrl} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      ) : (
                        <RiCameraLine size={24} style={{ color: "#94a3b8" }} />
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="logoFile"
                        className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1 mb-1"
                        style={{ cursor: "pointer", fontSize: "12px", fontWeight: 500 }}
                      >
                        {logoUrl ? "Changer de logo" : "Choisir un logo"}
                        <input type="file" id="logoFile" accept="image/*" style={{ display: "none" }} onChange={handleLogoChange} />
                      </label>
                      <p className="text-muted mb-0" style={{ fontSize: "11px" }}>PNG ou JPG, fond transparent recommandé.</p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold small text-dark mb-2 d-flex align-items-center gap-1">
                    <RiAwardLine size={16} style={{ color: "#009688" }} />
                    <span>Cachet officiel / Signature</span>
                  </label>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                        border: "1px dashed #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {cachetUrl ? (
                        <img src={cachetUrl} alt="Cachet" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      ) : (
                        <RiAwardLine size={24} style={{ color: "#94a3b8" }} />
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="cachetFile"
                        className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1 mb-1"
                        style={{ cursor: "pointer", fontSize: "12px", fontWeight: 500 }}
                      >
                        {cachetUrl ? "Changer de cachet" : "Ajouter un cachet"}
                        <input type="file" id="cachetFile" accept="image/*" style={{ display: "none" }} onChange={handleCachetChange} />
                      </label>
                      <p className="text-muted mb-0" style={{ fontSize: "11px" }}>Apparaîtra sur les devis et factures édités.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ONGLET 2 : MENTIONS LÉGALES & FACTURATION ── */}
        {subTab === "mentions" && (
          <div className="d-flex flex-column gap-3">
            <div
              className="p-4 rounded-4"
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #eef2f6",
                borderRadius: "16px",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "#009688", fontWeight: 700, fontSize: "16px" }}>
                <RiFileTextLine size={20} />
                <span>Mentions légales & Pied de page des factures</span>
              </div>
              <p className="text-muted small mb-4">
                Ces informations apparaîtront automatiquement au bas de vos devis et factures édités pour assurer leur conformité légale et fiscale.
              </p>

              <div className="row g-3">
                <div className="col-md-4">
                  <Controller
                    name="nina"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="NINA / NINEA complet" placeholder="Ex: 001234567 2G3" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-md-4">
                  <Controller
                    name="cofi"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="COFI (Code d'Ident. Fiscale)" placeholder="Ex: 087654321A" color="#0f172a" {...field} />
                    )}
                  />
                </div>
                <div className="col-md-4">
                  <Controller
                    name="imma_idt_tva"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="RCCM" placeholder="Ex: SN-DKR-2023-B-1234" color="#0f172a" {...field} />
                    )}
                  />
                </div>
              </div>

              <div className="row g-3 mt-2 pt-3 border-top">
                <div className="col-md-6">
                  <Controller
                    name="rib"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail
                        label="RIB / IBAN pour virement bancaire"
                        placeholder="Ex: SN08 SN01 0012 3456 7890 1234 56"
                        color="#0f172a"
                        {...field}
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="banque_nom"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <InputDetail label="Nom de l'établissement bancaire" placeholder="Ex: BOA, CBAO, Ecobank..." color="#0f172a" {...field} />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ONGLET 3 : COMPTABILITÉ & MAINTENANCE ── */}
        {subTab === "comptabilite" && (
          <div className="d-flex flex-column gap-4">
            {/* Numérotation des pièces */}
            <div
              className="p-4 rounded-4"
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #eef2f6",
                borderRadius: "16px",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "#009688", fontWeight: 700, fontSize: "16px" }}>
                <RiSettings4Line size={20} />
                <span>Format de numérotation des pièces</span>
              </div>
              <p className="text-muted small mb-3">
                Définissez la méthode de numérotation automatique de vos écritures et pièces justificatives.
              </p>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold small text-dark">Mode de numérotation</label>
                  <Controller
                    name="mode_numerotation_piece"
                    control={control}
                    defaultValue="PAR_JOURNAL"
                    render={({ field }) => (
                      <select {...field} className="form-select custom-input" style={{ borderRadius: "10px", padding: "10px 14px", fontSize: "14px" }}>
                        <option value="PAR_JOURNAL">Par journal auxiliaire (ex: OD-0001, AC-0001, VE-0001)</option>
                        <option value="CONTINU">Numérotation continue globale (ex: 000001, 000002...)</option>
                        <option value="PERSONNALISE">Format personnalisé (patron paramétrable)</option>
                      </select>
                    )}
                  />
                </div>

                {watchMode === "PERSONNALISE" && (
                  <div className="col-md-6">
                    <Controller
                      name="format_numerotation_piece"
                      control={control}
                      defaultValue="[JRN]-[AAAAMM]-[SEQ]"
                      render={({ field }) => (
                        <InputDetail
                          label="Patron de numérotation"
                          placeholder="Ex: [JRN]-[AAAAMM]-[SEQ]"
                          color="#0f172a"
                          {...field}
                        />
                      )}
                    />
                    <small className="text-muted d-block mt-1">
                      Balises supportées : [JRN] (code journal), [AAAA] (année), [MM] (mois), [SEQ] (séquenceur).
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Maintenance des écritures orphelines */}
            <div
              className="p-4 rounded-4"
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #eef2f6",
                borderRadius: "16px",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "#0f172a", fontWeight: 700, fontSize: "15px" }}>
                <RiEraserLine size={18} style={{ color: "#64748b" }} />
                <span>Maintenance et intégrité de la base</span>
              </div>
              <p className="text-muted small mb-3">
                Vérifiez et nettoyez les écritures orphelines (écritures comptables dont la transaction source a été supprimée).
              </p>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
                  onClick={handleCheckOrphans}
                  disabled={isCleanupLoading}
                >
                  <span>{isCleanupLoading ? "Analyse en cours..." : "Vérifier les écritures orphelines"}</span>
                </button>

                {orphansCount !== null && orphansCount > 0 && (
                  <>
                    {!showConfirm ? (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm rounded-pill px-3 py-2"
                        onClick={() => setShowConfirm(true)}
                      >
                        Nettoyer {orphansCount} écriture(s)
                      </button>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-danger small fw-bold">Confirmer la suppression définitive ?</span>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm rounded-pill px-3"
                          onClick={handleCleanupOrphans}
                          disabled={isCleanupLoading}
                        >
                          Oui, supprimer
                        </button>
                        <button
                          type="button"
                          className="btn btn-light btn-sm rounded-pill px-3"
                          onClick={() => setShowConfirm(false)}
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </>
                )}

                {orphansCount === 0 && (
                  <span className="text-success small fw-bold d-inline-flex align-items-center gap-1">
                    <RiCheckLine /> Base saine, aucune écriture orpheline.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── ONGLET 4 : SÉCURITÉ & 2FA ── */}
        {subTab === "securite" && (
          <div className="row">
            <div className="col-lg-9">
              <SecurityMFACard />
            </div>
          </div>
        )}

        {/* ── BARRE D'ACTIONS INFÉRIEURE (Sauvegarder / Annuler) ── */}
        {subTab !== "securite" && (
          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light rounded-pill px-4"
              style={{ fontSize: "14px", fontWeight: 500 }}
              onClick={handleCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn rounded-pill px-4 d-inline-flex align-items-center gap-2 shadow-sm"
              style={{
                backgroundColor: "#009688",
                borderColor: "#009688",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 600,
              }}
              disabled={isUpdating}
            >
              <RiCheckLine size={16} />
              <span>{isUpdating ? "Enregistrement..." : "Enregistrer les modifications"}</span>
            </button>
          </div>
        )}
            </div>
          </div>
        )}
      </form>

      {/* ── ONGLETS PARAMETRAGE (pages embarquees, hors formulaire) ── */}
      <div className="param-embedded" key={activeTab}>
        {activeTab === "comptes" && <ComptePaiements hideBack embedded />}
        {activeTab === "plan" && <PlanComptable dossier_id={id} embedded />}
        {activeTab === "migration" && <MigrationOdooPage hideBack embedded />}
        {activeTab === "journal" && <JournalPage hideBack embedded />}
        {activeTab === "partage" && dossierData?.user_role === 'ADMIN' && <PartageDossier hideBack embedded />}
      </div>
    </div>
  );
};

export default ParametrePage;
